import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Eye, FileDown, Send } from 'lucide-react';
import { Button, Card, CardTitle, CheckLabel } from './ui';
import ConfirmDialog from './ConfirmDialog';
import { PdfCoverPages } from './PdfPagesField';
import {
  fetchGeneratedPdfAvailable, fetchProgramPdf, generateProgramPdf, publishProgram, unpublishProgram,
} from './ProgramAdminApi';
import { PersonMeta, PanelHint } from './programAdminStyles';

// The Publish area (plan §7.6 decision 9, §7.7 item 9): whether this event's program is public, and
// whether the chosen program PDF is public too. Publishing asks for that answer (ProgramController
// requires `pdfPublic`), so the checkbox is part of the publish action, not a separate switch; a
// program may be nothing but its PDF (P10a), which is why publishing one with a public PDF is
// allowed without any entry. The first publish goes straight through; a second one is confirmed in
// the shared ConfirmDialog first, because it changes what the public sees at once. Unpublishing
// answers the settings unchanged. `onChanged` reloads the whole screen afterwards; the public
// preview is a new tab that renders the draft with the public section component.
// "Make the program PDF" (once published) has the server print the public program in the website's
// design (programimport.GeneratedProgramPdf) and makes that PDF the public program PDF; it is offered
// only when the server has a PDF renderer, and has to be done again after changes to the program.
// Above it the PDF's title pages (PdfPagesField, also in the Program area), which only that PDF uses.
const PublishArea = ({ eventId, program, onChanged, onPreviewPublic }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const t = useCallback((id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values), [intl]);
  const settings = program?.event || {};
  const [pdf, setPdf] = useState(null);
  const [pdfPublic, setPdfPublic] = useState(!!settings.programPdfPublic);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [rendererAvailable, setRendererAvailable] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadPdf = useCallback(() => fetchProgramPdf(eventId)
    .then((choice) => setPdf(choice))
    .catch(() => setPdf(null)), [eventId]);

  useEffect(() => {
    let live = true;
    fetchProgramPdf(eventId)
      .then((choice) => { if (live) setPdf(choice); })
      .catch(() => { if (live) setPdf(null); });
    fetchGeneratedPdfAvailable(eventId)
      .then((available) => { if (live) setRendererAvailable(available); })
      .catch(() => { if (live) setRendererAvailable(false); });
    return () => { live = false; };
  }, [eventId]);

  useEffect(() => { setPdfPublic(!!settings.programPdfPublic); }, [settings.programPdfPublic]);

  // The backend's own message is what the admin reads (a 409 carries its plain-text reason); a 401
  // goes to the login like on every other screen. Null: nothing to show, the redirect happened.
  const textOf = (err, fallback) => {
    if (err?.response?.status === 401) { navigate('/admin/login'); return null; }
    return (typeof err?.response?.data === 'string' && err.response.data) || fallback;
  };

  // One publish call for both entries into it: the button publishes a draft straight away, the
  // dialog confirms a second publish first (`confirming`). A refusal stays where it was asked - in
  // the dialog, which keeps the answer the admin gave, or under the card.
  const publish = async (confirming) => {
    setBusy(true); setMessage(null); setDialogError(null);
    try {
      await publishProgram(eventId, pdfPublic);
      setConfirmOpen(false);
      setMessage({ text: t('programAdmin.publish.published', 'The program is published.') });
      await onChanged?.();
    } catch (err) {
      const text = textOf(err, t('programAdmin.publish.failed', 'Could not publish the program.'));
      if (text == null) return;
      if (confirming) setDialogError(text);
      else setMessage({ error: true, text });
    } finally { setBusy(false); }
  };

  const unpublish = async () => {
    setBusy(true); setMessage(null);
    try {
      await unpublishProgram(eventId);
      setMessage({ text: t('programAdmin.publish.unpublished', 'The program is a draft again.') });
      await onChanged?.();
    } catch (err) {
      const text = textOf(err, t('programAdmin.publish.unpublishFailed', 'Could not unpublish the program.'));
      if (text != null) setMessage({ error: true, text });
    } finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true); setGenerating(true); setMessage(null);
    try {
      const result = await generateProgramPdf(eventId);
      setMessage({
        text: t('programAdmin.publish.generated', 'The program PDF is made and public in German and English: {name}, {enName}',
          { name: result.fileName, enName: result.enFileName }),
      });
      await loadPdf();
      await onChanged?.();
    } catch (err) {
      const text = textOf(err, t('programAdmin.publish.generateFailed', 'Could not make the program PDF.'));
      if (text != null) setMessage({ error: true, text });
    } finally { setBusy(false); setGenerating(false); }
  };

  const published = settings.programStatus === 'PUBLISHED';

  return (
    <Card>
      <CardTitle><FormattedMessage id="programAdmin.area.publish" defaultMessage="Publish" /></CardTitle>

      {message && (
        <p style={{ margin: '0 0 1rem', padding: '.6rem .7rem', background: message.error ? '#fee2e2' : '#dcfce7', color: message.error ? '#991b1b' : '#166534', fontSize: '.85rem' }}>
          {message.text}
        </p>
      )}

      <p style={{ margin: '0 0 1rem', fontSize: '.9rem', color: '#334155' }}>
        {published
          ? <FormattedMessage id="programAdmin.publish.statePublished" defaultMessage="This event's program is public." />
          : <FormattedMessage id="programAdmin.publish.stateDraft" defaultMessage="This event's program is a draft; the public section does not show it." />}
        {settings.programPublishedAt && (
          <> {t('programAdmin.publish.since', 'Published {when}', { when: intl.formatDate(new Date(settings.programPublishedAt), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) })}</>
        )}
      </p>

      <PersonMeta style={{ marginBottom: '.6rem' }}>
        <FormattedMessage id="programAdmin.publish.pdf" defaultMessage="Program PDF: {name}" values={{ name: pdf?.fileName || t('programAdmin.publish.noPdf', 'none chosen') }} />
      </PersonMeta>

      <CheckLabel style={{ fontWeight: 600, color: '#334155', marginBottom: '1rem' }}>
        <input type="checkbox" checked={pdfPublic} onChange={(e) => setPdfPublic(e.target.checked)} />
        <span><FormattedMessage id="programAdmin.publish.pdfPublic" defaultMessage="Offer the chosen program PDF for download too" /></span>
      </CheckLabel>

      <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
        <Button type="button" disabled={busy} onClick={() => (published ? setConfirmOpen(true) : publish(false))}>
          <Send size={15} aria-hidden="true" />
          <FormattedMessage id="programAdmin.publish.do" defaultMessage="Publish" />
        </Button>
        <Button type="button" secondary disabled={busy || !published} onClick={unpublish}>
          <FormattedMessage id="programAdmin.publish.undo" defaultMessage="Unpublish" />
        </Button>
        <Button type="button" secondary disabled={busy} onClick={onPreviewPublic}>
          <Eye size={15} aria-hidden="true" />
          <FormattedMessage id="programAdmin.previewPublic" defaultMessage="Preview public program" />
        </Button>
      </div>

      {rendererAvailable && (
        <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
          <p style={{ margin: '0 0 .6rem', fontSize: '.9rem', color: '#334155' }}>
            <FormattedMessage
              id="programAdmin.publish.generateIntro"
              defaultMessage="Make the public program PDF from the program itself, in the website's design. It replaces the chosen program PDF and is offered for download at once. Make it again after changing the program."
            />
          </p>
          <PdfCoverPages eventId={eventId} />
          <Button type="button" disabled={busy || !published} onClick={generate}>
            <FileDown size={15} aria-hidden="true" />
            {generating
              ? <FormattedMessage id="programAdmin.publish.generating" defaultMessage="Making the PDF…" />
              : <FormattedMessage id="programAdmin.publish.generate" defaultMessage="Make and publish the program PDF" />}
          </Button>
          {!published && (
            <PanelHint>
              <FormattedMessage id="programAdmin.publish.generateNeedsPublish" defaultMessage="Publish the program first." />
            </PanelHint>
          )}
        </div>
      )}

      <PanelHint>
        <FormattedMessage id="programAdmin.publish.rule"
          defaultMessage="Public visibility rule: confirmed people, and people explicitly shown - nothing else." />
      </PanelHint>

      {/* A second publish changes what the public sees at once - the new PDF answer and the tree as it
          stands - so it is asked in the one confirmation dialog of this screen (P11) first. A refusal
          (409: no entry and no public PDF left) comes back into the dialog, as on every other action. */}
      <ConfirmDialog
        open={confirmOpen}
        title={t('programAdmin.publish.again.title', 'Publish the program again?')}
        intro={t('programAdmin.publish.again.intro', 'The program is already public; the changes go live at once.')}
        confirmLabel={t('programAdmin.publish.do', 'Publish')}
        error={dialogError}
        busy={busy}
        onConfirm={() => publish(true)}
        onClose={() => { if (!busy) { setConfirmOpen(false); setDialogError(null); } }}
      />
    </Card>
  );
};

export default PublishArea;
